/**
 * PlayerContext - Global Video Player State Management
 *
 * Manages the Bitmovin video player state across the entire application.
 * Provides centralized control for video playback, fullscreen, and overlay states.
 *
 * Key architectural decisions:
 * - Context over prop drilling: Player controls needed in multiple components
 *   (header controls, player UI, playlist navigation, mobile views)
 * - Global state for single player instance: Prevents multiple videos playing
 *   simultaneously and ensures consistent playback state
 * - Separation from server state: Player state is purely client-side UI state,
 *   while video metadata fetched via React Query
 *
 * Business rules:
 * - Single video instance: Only one video can play at a time (UX requirement)
 * - Analytics tracking: All player events tracked for engagement metrics
 * - Mobile-specific handling: Different close behaviors for mobile vs desktop
 * - Chat bot integration: Hide chat when player is active (avoid UI overlap)
 *
 * Performance considerations:
 * - State batching: Multiple state updates in handlers to reduce re-renders
 * - No expensive computations: All state updates are simple assignments
 * - Memory cleanup: Player resources cleaned up on unmount via handleClose
 *
 * @see src/features/player/VideoPlayer.jsx - Main player component
 * @see src/features/player/PlayerOverlay.jsx - Player controls UI
 * @see .claude/state-management-rules.md - State management patterns
 */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  Dispatch,
  SetStateAction,
} from 'react';
import { useChatBot } from '../hooks/useChatBot';
import { useSessionAnalytics } from './SessionContext';
import { useNavigate } from 'react-router-dom';
import useScreenOrientation, { OrientationType } from '../hooks/useScreenOrientation';

/**
 * Player context value interface
 *
 * WHY so many properties:
 * - Different components need different state subsets
 * - Granular updates reduce unnecessary re-renders
 * - Component optimization relies on state selection
 */
export interface PlayerContextValue {
  /** Player visibility state */
  showPlayer: boolean;
  setShowPlayer: Dispatch<SetStateAction<boolean>>;

  /** HLS/DASH manifest URL */
  streamUrl: string | null;
  setStreamUrl: Dispatch<SetStateAction<string | null>>;

  /** Player error state */
  error: string | null;
  setError: Dispatch<SetStateAction<string | null>>;

  /** Autoplay control */
  autoPlay: boolean;
  setAutoPlay: Dispatch<SetStateAction<boolean>>;

  /** Live stream flag */
  isLiveStream: boolean;
  setIsLiveStream: Dispatch<SetStateAction<boolean>>;

  /** Stream identification */
  streamId: string | null;
  setStreamId: Dispatch<SetStateAction<string | null>>;

  /** Previous stream ID (for navigation) */
  prevStreamId: string | null;

  /** End-of-stream state */
  hasEnded: boolean;
  setHasEnded: Dispatch<SetStateAction<boolean>>;

  /** Overlay controls visibility */
  showOverlay: boolean;
  setShowOverlay: Dispatch<SetStateAction<boolean>>;

  /** Pause state */
  isPaused: boolean;
  setIsPaused: Dispatch<SetStateAction<boolean>>;

  /** Touch interaction tracking */
  hasTouched: boolean;
  setHasTouched: Dispatch<SetStateAction<boolean>>;

  /** Actual playback state */
  isPlaying: boolean;
  setIsPlaying: Dispatch<SetStateAction<boolean>>;

  /** Fullscreen state */
  isFullscreen: boolean;
  setIsFullscreen: Dispatch<SetStateAction<boolean>>;

  /** Playback position (seconds) */
  playerTime: number;
  setPlayerTime: Dispatch<SetStateAction<number>>;

  /**
   * Initiates video playback
   * @param streamUrl - HLS/DASH manifest URL
   * @param autoPlay - Whether to start playing immediately
   */
  handlePlay: (streamUrl: string, autoPlay?: boolean) => void;

  /** Closes player (desktop) - with navigation to home */
  handleClose: () => void;

  /** Closes player (mobile) - stays on current page */
  handleCloseMobile: () => void;

  /**
   * Handles play/pause interaction
   * @param isPaused - Current pause state
   */
  handlePlayPauseClick: (isPaused: boolean) => void;

  /**
   * Updates playback time
   * @param time - Current playback time in seconds
   */
  handleTimeChange: (time: number) => void;
}

/**
 * Create context with undefined default
 */
const PlayerContext = createContext<PlayerContextValue | undefined>(undefined);

/**
 * PlayerProvider props interface
 */
export interface PlayerProviderProps {
  /** Child components that need player state access */
  children: ReactNode;
}

/**
 * PlayerProvider component that manages global video player state.
 *
 * Why separate state variables instead of a single state object:
 * - Granular updates: Each state change triggers minimal re-renders
 * - React batching: Multiple setState calls in event handlers are batched
 * - Component optimization: Child components can subscribe to specific states
 *
 * @param props - Provider props
 */
export function PlayerProvider({ children }: PlayerProviderProps): JSX.Element {
  // WHY: Core player visibility - controls whether player UI is rendered
  // Prevents resource allocation until user initiates playback
  const [showPlayer, setShowPlayer] = useState<boolean>(false);

  // WHY: Stream URL management - HLS/DASH manifest URL for Bitmovin player
  // Null when no video loaded, prevents player initialization errors
  const [streamUrl, setStreamUrl] = useState<string | null>(null);

  // WHY: Error state for user feedback - displays player-specific errors
  // Separate from API errors (handled by React Query)
  const [error, setError] = useState<string | null>(null);

  // WHY: Autoplay control - respects browser autoplay policies
  // Some browsers block autoplay without user interaction
  const [autoPlay, setAutoPlay] = useState<boolean>(false);

  // WHY: Live vs VOD distinction - different UI controls and behaviors
  // Live streams disable seeking, show "LIVE" indicator
  const [isLiveStream, setIsLiveStream] = useState<boolean>(false);

  // WHY: Stream identification for analytics - tracks what content is playing
  // Used for engagement metrics, watch time, completion rates
  const [streamId, setStreamId] = useState<string | null>(null);
  const [prevStreamId, setPrevStreamId] = useState<string | null>(null);

  // WHY: End-of-stream state - triggers next video in playlist or recommendations
  // Separate from isPaused to distinguish natural end vs user pause
  const [hasEnded, setHasEnded] = useState<boolean>(false);

  // WHY: Overlay controls visibility - auto-hides after inactivity
  // Better UX for fullscreen viewing, especially on mobile
  const [showOverlay, setShowOverlay] = useState<boolean>(true);

  // WHY: Pause state tracking - controls play/pause button UI
  // Separate from isPlaying for buffering states
  const [isPaused, setIsPaused] = useState<boolean>(false);

  // WHY: Touch interaction tracking - mobile-specific UX optimizations
  // First touch shows controls, second touch performs action
  const [hasTouched, setHasTouched] = useState<boolean>(false);

  // WHY: Actual playback state - true when video is actively playing
  // Differs from !isPaused during buffering or seeking
  const [isPlaying, setIsPlaying] = useState<boolean>(false);

  // WHY: Fullscreen state - controls UI layout and escape key handling
  // Tracked separately from browser fullscreen API for reliability
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

  // WHY: Playback position tracking - for resume playback, analytics
  // Persisted to enable "continue watching" feature
  const [playerTime, setPlayerTime] = useState<number>(0);

  // WHY: Screen orientation tracking - mobile landscape video optimization
  // Auto-fullscreen on landscape, exit on portrait
  const [orientation, setOrientation] = useState<OrientationType | null>(null);

  const { updateChatBot } = useChatBot();
  const navigate = useNavigate();
  const screenOrientation = useScreenOrientation();

  // WHY: Track previous stream for playlist navigation and analytics
  // Enables "previously watched" features and back navigation
  useEffect(() => {
    if (streamId) setPrevStreamId(streamId);
  }, [streamId]);

  // WHY: Analytics tracking for playback start events
  // Captures engagement metrics when user starts watching content
  // Only fires when both player is shown AND streamId is set
  const { sendMessage } = useSessionAnalytics();

  useEffect(() => {
    if (showPlayer && streamId) {
      sendMessage('playerStart', { streamId });
    }
  }, [streamId, showPlayer, sendMessage]);

  // WHY: Orientation change handling for mobile video UX
  // Auto-hides controls when rotating device (user wants fullscreen)
  // Prevents controls blocking video during orientation transition
  useEffect(() => {
    if (orientation && orientation !== screenOrientation) {
      setShowOverlay(false);
    }
    setOrientation(screenOrientation);
  }, [screenOrientation, orientation]);

  // WHY: Initialize overlay as visible for discoverability
  // Users need to see controls initially to understand player interface
  useEffect(() => {
    setShowOverlay(true);
  }, []);

  /**
   * Handles desktop player close with complete cleanup.
   *
   * WHY this extensive cleanup process:
   * - Memory management: Release video buffer and player resources
   * - State reset: Ensure clean state for next video playback
   * - Analytics: Track user engagement and video completion
   * - UI restoration: Re-enable chat bot and remove player CSS classes
   * - Navigation: Return to home page after closing video
   *
   * Order matters: Analytics sent before state reset to capture final state
   */
  function handleClose(): void {
    // WHY: Remove body class to restore normal page scrolling
    // Player adds this class to prevent background scrolling
    document.body.classList.remove('player-showing');

    // WHY: Re-enable chat bot after video closes
    // Chat is hidden during playback to avoid UI overlap
    updateChatBot({
      hideDefaultLauncher: false,
    });

    // WHY: Send analytics before state reset
    // Captures final playback position and duration
    sendMessage('playerStop', { streamId });

    // WHY: Reset ALL player state to initial values
    // Prevents stale state bugs when opening new videos
    setShowPlayer(false);
    setPlayerTime(0);
    setIsFullscreen(false);
    setIsPlaying(false);
    setShowOverlay(true);
    setHasTouched(false);
    setIsPaused(false);
    setAutoPlay(false);
    setStreamUrl(null);
    setError(null);

    // WHY: Navigate to home with replace to prevent back button issues
    // Users shouldn't navigate "back" to a closed video
    navigate('/', { replace: true });
  }

  /**
   * Handles mobile-specific player close without navigation.
   *
   * WHY separate mobile handler:
   * - Mobile users stay on current page (different UX expectation)
   * - Mobile has different navigation patterns (swipe gestures)
   * - Preserves page context for mobile browsing continuity
   */
  function handleCloseMobile(): void {
    document.body.classList.remove('player-showing');
    setShowPlayer(false);

    updateChatBot({
      hideDefaultLauncher: false,
    });

    sendMessage('playerStop', { streamId });

    // WHY: Same cleanup as desktop but NO navigation
    // Mobile users expect to stay on the same page
    setIsFullscreen(false);
    setIsPlaying(false);
    setShowOverlay(true);
    setHasTouched(false);
    setIsPaused(false);
    setAutoPlay(false);
    setStreamUrl(null);
    setError(null);
    setPlayerTime(0);
  }

  /**
   * Initiates video playback with specified URL.
   *
   * WHY this initialization sequence:
   * - Body class for CSS-based layout adjustments
   * - Chat bot hiding to maximize video viewing area
   * - Error clearing for fresh playback attempt
   * - State setting order ensures player renders correctly
   *
   * @param streamUrl - HLS/DASH manifest URL
   * @param autoPlay - Whether to start playing immediately
   */
  function handlePlay(streamUrl: string, autoPlay: boolean = true): void {
    // WHY: Add class for CSS adjustments (hide scrollbars, adjust z-index)
    document.body.classList.add('player-showing');

    // WHY: Hide chat bot during video playback
    // Prevents UI overlap and distraction during viewing
    updateChatBot({
      hideDefaultLauncher: true,
    });

    // WHY: Clear previous errors for fresh playback attempt
    setError(null);

    // WHY: Set URL and autoplay before showing player
    // Player component reads these on mount
    setStreamUrl(streamUrl);
    setAutoPlay(autoPlay);
    setShowPlayer(true);
  }

  /**
   * Handles play/pause user interaction.
   *
   * WHY track touch separately from pause state:
   * - Mobile UX: First touch shows controls, second performs action
   * - Overlay visibility tied to pause state for better UX
   * - Touch tracking enables mobile-specific interactions
   */
  function handlePlayPauseClick(isPaused: boolean): void {
    setHasTouched(true);
    // WHY: Show overlay when paused so user can see play button
    setShowOverlay(isPaused);
    setIsPaused(isPaused);
  }

  /**
   * Updates current playback time for tracking.
   *
   * WHY track time in context:
   * - Resume playback: Remember position for "continue watching"
   * - Analytics: Track engagement and drop-off points
   * - UI updates: Progress bar and time display
   */
  function handleTimeChange(time: number): void {
    setPlayerTime(time);
  }

  const value: PlayerContextValue = {
    // WHY expose all these values: Different components need different subsets
    // Header needs showPlayer, VideoPlayer needs streamUrl, Overlay needs controls
    // Exposing granular state prevents unnecessary re-renders
    showPlayer,
    setShowPlayer,
    streamUrl,
    setStreamUrl,
    error,
    setError,
    autoPlay,
    setAutoPlay,
    handlePlay,
    handleClose,
    handleCloseMobile,
    setIsLiveStream,
    isLiveStream,
    setStreamId,
    setHasEnded,
    hasEnded,
    showOverlay,
    setShowOverlay,
    isPaused,
    setIsPaused,
    setHasTouched,
    hasTouched,
    handlePlayPauseClick,
    isPlaying,
    setIsPlaying,
    isFullscreen,
    setIsFullscreen,
    setPlayerTime,
    playerTime,
    handleTimeChange,
    prevStreamId,
  };

  return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
}

/**
 * Custom hook to access player context state and methods.
 *
 * WHY custom hook instead of direct context export:
 * - Error boundary: Catches misuse outside provider with clear error message
 * - Type safety: Can add TypeScript types in one place
 * - Abstraction: Can add validation or transformations if needed
 *
 * @returns Player state and control methods
 * @throws {Error} If used outside PlayerProvider
 */
export function usePlayer(): PlayerContextValue {
  const context = useContext(PlayerContext);

  // WHY: Fail fast with clear error message for developer experience
  // Common mistake is using hook outside the provider tree
  if (context === undefined) {
    throw new Error('usePlayer must be used within PlayerProvider');
  }

  return context;
}
